import _ from 'lodash';
import {
    ProductAttributes,
    ProductAttributesField,
    ProductData,
    ProductDetailsField,
    ProductField,
    ProductInCatalog,
    ProductType,
    UOM,
} from '@treez/commons/sharedTypings/product';
import {ImageAttributes} from '@treez/commons//sharedTypings';
import {MergeState} from '~/redux/reducers/MergeReducer';
import {GlobalApplicationState} from '~/redux/reducers/RootReducer';
import {ProductsPresenceAcrossStores, ProductStorePresenceAttributes} from '~/utils/ProductAPI';

const {
    AMOUNT,
    ATTRIBUTES,
    BRAND_NAME,
    CLASSIFICATION,
    DESCRIPTIONS,
    ECOMMERCE_NAME,
    IMAGES,
    NAME,
    SIZE,
    SKU,
    SUBTYPE,
    TYPE,
} = ProductField;

const {
    DOSES,
    CBD_MG,
    THC_MG,
    DOSE_CBD_MG,
    DOSE_THC_MG,
    EXTRACTION_METHOD,
    STRAIN,
    NET_WEIGHT,
    GROSS_WEIGHT,
    TOTAL_WEIGHT,
    TOTAL_CONCENTRATE_WEIGHT
} = ProductDetailsField;

const {FLAVOR, GENERAL, EFFECT, INGREDIENT, INTERNAL} = ProductAttributesField;

export const selectMergeState = ({mergeState}: GlobalApplicationState): MergeState => mergeState;

export const selectMergingToOrgProduct = ({mergeState}: GlobalApplicationState): boolean => {
    return mergeState.mergingToOrgProduct;
};

export const selectProductToMergeToStorePresence = ({
    mergeState,
}: GlobalApplicationState):
    | ProductStorePresenceAttributes[]
    | Partial<ProductStorePresenceAttributes>[] => {
    return mergeState.productToMergeToStorePresence;
};

export const selectMergeExactMatchFields = ({
    mergeState,
}: GlobalApplicationState): ProductData[] => {
    return _.get(mergeState, 'exactMatchedFields', []) as ProductData[];
};

export const selectMergeProductsStorePresence = ({
    mergeState,
}: GlobalApplicationState):
    | Partial<ProductsPresenceAcrossStores>
    | ProductsPresenceAcrossStores => {
    return _.get(mergeState, 'storePresence') as
        | Partial<ProductsPresenceAcrossStores>
        | ProductsPresenceAcrossStores;
};

export const selectMergeFormDirtyStatus = ({mergeState}: GlobalApplicationState): boolean => {
    return mergeState.productFormDirty;
};

export const selectMergeProduct = ({
    mergeState,
}: GlobalApplicationState): ProductInCatalog | Partial<ProductInCatalog> => {
    return (
        _.get(mergeState, 'productToMergeTo', {}) ||
        ({} as ProductInCatalog | Partial<ProductInCatalog>)
    );
};

export const selectMergeProductImages = ({
    mergeState,
}: GlobalApplicationState): ImageAttributes[] | undefined => {
    return _.get(mergeState, 'productToMergeTo.images', []) || ({} as ImageAttributes[]);
};

export const selectMergeProductName = ({
    mergeState,
}: GlobalApplicationState): string | undefined => {
    return _.get(mergeState, 'productToMergeTo.name') as string | undefined;
};

export const selectMergeProductBrandName = ({
    mergeState,
}: GlobalApplicationState): string | undefined => {
    return _.get(mergeState, 'productToMergeTo.brandName') as string | undefined;
};

export const selectMergeProductEcommerceName = ({
    mergeState,
}: GlobalApplicationState): string | undefined => {
    return _.get(mergeState, 'productToMergeTo.eCommerceName') as string | undefined;
};

export const selectMergeProductType = ({
    mergeState,
}: GlobalApplicationState): ProductType | undefined => {
    return _.get(mergeState, 'productToMergeTo.type') as ProductType | undefined;
};

export const selectMergeProductSubtype = ({
    mergeState,
}: GlobalApplicationState): string | undefined => {
    return _.get(mergeState, 'productToMergeTo.subtype') as string | undefined;
};

export const selectMergeProductSize = ({
    mergeState,
}: GlobalApplicationState): string | undefined => {
    return _.get(mergeState, 'productToMergeTo.size') as string | undefined;
};

export const selectMergeProductAmount = ({
    mergeState,
}: GlobalApplicationState): string | undefined => {
    return _.get(mergeState, 'productToMergeTo.amount') as string | undefined;
};

export const selectMergeProductClassification = ({
    mergeState,
}: GlobalApplicationState): string | undefined => {
    return _.get(mergeState, 'productToMergeTo.classification') as string | undefined;
};

export const selectMergeProductUOM = ({mergeState}: GlobalApplicationState): UOM | undefined => {
    return _.get(mergeState, 'productToMergeTo.uom') as UOM | undefined;
};

export const selectMergeProductSKU = ({mergeState}: GlobalApplicationState): string | undefined => {
    return _.get(mergeState, 'productToMergeTo.sku') as string | undefined;
};

export const selectMergeProductDoses = ({mergeState}: GlobalApplicationState): number | undefined =>
    _.get(mergeState.productToMergeTo, 'details.doses') as number | undefined;

export const selectMergeProductCBD = ({mergeState}: GlobalApplicationState): number | undefined =>
    _.get(mergeState.productToMergeTo, 'details.cbd') as number | undefined;

export const selectMergeProductTHC = ({mergeState}: GlobalApplicationState): number | undefined =>
    _.get(mergeState.productToMergeTo, 'details.thcMg') as number | undefined;

export const selectMergeProductDoseCBD = ({
    mergeState,
}: GlobalApplicationState): number | undefined =>
    _.get(mergeState.productToMergeTo, 'details.doseCbdMg') as number | undefined;

export const selectMergeProductDoseTHC = ({
    mergeState,
}: GlobalApplicationState): number | undefined =>
    _.get(mergeState.productToMergeTo, 'details.doseThcMg') as number | undefined;

export const selectMergeProductExtractionMethod = ({
    mergeState,
}: GlobalApplicationState): string | undefined =>
    _.get(mergeState, 'details.extractionMethod') as string | undefined;

export const selectMergeProductStrain = ({
    mergeState,
}: GlobalApplicationState): string | undefined =>
    _.get(mergeState, 'details.strain') as string | undefined;

export const selectMergeProductNetWeight = ({
    mergeState,
}: GlobalApplicationState): number | undefined =>
    _.get(mergeState, 'details.netWeight') as number | undefined;

export const selectMergeProductGrossWeight = ({
    mergeState,
}: GlobalApplicationState): number | undefined =>
    _.get(mergeState, 'details.grossWeight') as number | undefined;

export const selectMergeProductTotalWeight = ({
    mergeState,
}: GlobalApplicationState): number | undefined =>
    _.get(mergeState, 'details.totalWeight') as number | undefined;

export const selectMergeProductTotalConcentrateWeight = ({
    mergeState,
}: GlobalApplicationState): number | undefined =>
    _.get(mergeState, 'details.totalConcentrateWeight') as number | undefined;

export const selectMergeProductDescription = ({
    mergeState,
}: GlobalApplicationState): string | undefined =>
    _.get(mergeState.productToMergeTo, 'descriptions.main');

export const selectMergeProductAttributes = ({
    mergeState,
}: GlobalApplicationState): ProductAttributes | undefined => mergeState.productToMergeTo.attributes;

export const selectMergeProductEffects = ({
    mergeState,
}: GlobalApplicationState): string[] | undefined => {
    const attributes = _.get(mergeState.productToMergeTo, ATTRIBUTES);

    return _.get(attributes, EFFECT, []);
};

export const selectProductFieldSources = ({
    mergeState,
}: GlobalApplicationState): Partial<Record<ProductField, Partial<ProductInCatalog>>> => {
    return mergeState.productFieldSources;
};

export const selectMergeProductFlavors = ({
    mergeState,
}: GlobalApplicationState): string[] | undefined => {
    const attributes = _.get(mergeState.productToMergeTo, ATTRIBUTES);

    return _.get(attributes, FLAVOR, []);
};

export const selectMergeProductIngredients = ({
    mergeState,
}: GlobalApplicationState): string[] | undefined => {
    const attributes = _.get(mergeState.productToMergeTo, ATTRIBUTES);

    return _.get(attributes, INGREDIENT, []);
};

export const selectMergeProductGeneralAttributes = ({
    mergeState,
}: GlobalApplicationState): string[] | undefined => {
    const attributes = _.get(mergeState.productToMergeTo, ATTRIBUTES);

    return _.get(attributes, GENERAL, []);
};

export const selectMergeProductInternalTags = ({
    mergeState,
}: GlobalApplicationState): string[] | undefined => {
    const attributes = _.get(mergeState.productToMergeTo, ATTRIBUTES);

    return _.get(attributes, INTERNAL, []);
};

export const mergeProductSelectorMap: Partial<Record<ProductData, any>> = {
    [AMOUNT]: selectMergeProductAmount,
    [BRAND_NAME]: selectMergeProductBrandName,
    [CBD_MG]: selectMergeProductCBD,
    [CLASSIFICATION]: selectMergeProductClassification,
    [DESCRIPTIONS]: selectMergeProductDescription,
    [DOSE_CBD_MG]: selectMergeProductDoseCBD,
    [DOSE_THC_MG]: selectMergeProductDoseTHC,
    [DOSES]: selectMergeProductDoses,
    [ECOMMERCE_NAME]: selectMergeProductEcommerceName,
    [EFFECT]: selectMergeProductEffects,
    [EXTRACTION_METHOD]: selectMergeProductExtractionMethod,
    [FLAVOR]: selectMergeProductFlavors,
    [GENERAL]: selectMergeProductGeneralAttributes,
    [GROSS_WEIGHT]: selectMergeProductGrossWeight,
    [IMAGES]: selectMergeProductImages,
    [INGREDIENT]: selectMergeProductIngredients,
    [INTERNAL]: selectMergeProductInternalTags,
    [NAME]: selectMergeProductName,
    [NET_WEIGHT]: selectMergeProductNetWeight,
    [ProductField.UOM]: selectMergeProductUOM,
    [SIZE]: selectMergeProductSize,
    [SKU]: selectMergeProductSKU,
    [STRAIN]: selectMergeProductStrain,
    [SUBTYPE]: selectMergeProductSubtype,
    [THC_MG]: selectMergeProductTHC,
    [TOTAL_WEIGHT]: selectMergeProductTotalWeight,
    [TYPE]: selectMergeProductType,
    [TOTAL_CONCENTRATE_WEIGHT]: selectMergeProductTotalConcentrateWeight,
};

export const getFieldSelector = (field: ProductData) => {
    return mergeProductSelectorMap[field];
};
